/**
 * @description This is a singleton DB, only one copy per app can be
 * produced.
 */
import {
  dbAction,
  DBOpenEvent,
  DBRecord,
  ExtendedError,
  ExtendedErrorOptions,
  IDBStaus,
  IndexDBRequest,
  IndexDBResult,
  StoreName,
} from "./index-db-models";

export class IndexDbService {
  /**
   * @settings:
   *
   */
  private static readonly Config = {
    mainDbName: "HarshamaDB",
    versionNumber: 1,
    StoreNames: [],
    createIdIfMissing: false,
    IdsCanOnlyBeStrings: true,
  };
  private static instance: IndexDbService;
  private static readonly versionNumber = IndexDbService.Config.versionNumber;
  private MAIN_DB_NAME = IndexDbService.Config.mainDbName;
  private readonly MAIN_STORE_NAME: StoreName = "NoalGlobalData";
  private allStores: StoreName[] = [
    "NoalGlobalData",
    "Cache",
    "GeneralData",
    "Translations",
    "manageDd",
  ];
  private MANAGEMENT_STORE_NAME: StoreName = "manageDd";
  private mainStore: IDBObjectStore | null = null;
  private mainDb: IDBDatabase | null = null;
  private dbTransaction: IDBTransaction | null = null;
  private idbStatus: IDBStaus = IDBStaus.pending;

  private constructor() {
    if (!("indexedDB" in window)) {
      // tslint:disable-next-line:no-console
      console.warn("IndexedDB not supported");
      this.idbStatus = IDBStaus.error;
    } else {
      this.initDB().then(
        (db) => {
          this.mainDb = db as IDBDatabase;
          this.idbStatus = IDBStaus.success;
        },
        (err) => {
          this.idbStatus = IDBStaus.error;
          this.dBError(err);
        }
      );
    }
  }

  public static get Instance(): IndexDbService {
    if (!IndexDbService.instance || true) {
      IndexDbService.instance = new IndexDbService();
    }
    return IndexDbService.instance;
  }

  public get dbStatus(): IDBStaus {
    return this.idbStatus;
  }

  public get defaultStore(): StoreName {
    return this.MAIN_STORE_NAME;
  }

  private get allStoreNames(): string[] {
    return [this.MAIN_STORE_NAME, this.MANAGEMENT_STORE_NAME];
  }

  private get newId(): number | string {
    const rng = 1000;
    const numId = Math.floor(Math.random() * rng);
    if (IndexDbService.Config.IdsCanOnlyBeStrings) {
      return numId.toString();
    }
    return numId;
  }

  public ready(maxTimeToWait: number = 100): Promise<boolean | Error> {
    //maxTimeToWait = 2000
    return Promise.race([
      this.timer(maxTimeToWait, false),
      this.returnWhenDBReady(),
    ]);
  }

  public returnWhenDBReady(count: number = 0): Promise<boolean | Error> {
    if (this.dbStatus === IDBStaus.success) {
      return new Promise((resolve) => resolve(true));
    } else if (this.dbStatus === IDBStaus.pending) {
      if (count > 4) {
        return new Promise((res, rej) => {
          rej(false);
        });
      }
      return new Promise<boolean | Error>((res) => {
        setTimeout((_) => {
          return res(this.returnWhenDBReady(count++));
        }, 100);
      });
    } else if (this.dbStatus === IDBStaus.error) {
      return new Promise((res, rej) => {
        rej(false);
      });
    }
  }

  buildExtendedErrorData(
    data: IndexDBResult<IDBRequest, Error>
  ): ExtendedErrorOptions {
    const extendedErrorOp: ExtendedErrorOptions = {};
    try {
      if (data.actionRequested) {
        const actionRequested: IndexDBRequest = data.actionRequested;
        const id = actionRequested.data ? actionRequested.data.id : "";
        const actionType = actionRequested.actionType;
        const store = actionRequested.storeName;
        let verb = "from";
        switch (actionType) {
          case "add":
            verb = "to";
            break;
          case "put":
            verb = "in";
            break;
          case "get":
            verb = "from";
            break;
          case "clear":
            verb = " ";
            break;
        }
        if (actionType === "add" || "put") {
        }
        const idData = id ? "id: " + id : "";
        extendedErrorOp.postMessage = ` ('${actionType}' ${verb} store '${store}' ${idData})`;
      }
      const moreinfo: any = {};
      Object.keys(data).forEach((key) => {
        if (key !== "error") {
          moreinfo[key] = (data as any)[key];
        }
      });
      extendedErrorOp.info = moreinfo;
    } catch (e) {
      extendedErrorOp.info = {
        message: "internal error in the buildExtendedErrorData function",
        err: e,
      };
    }

    return extendedErrorOp;
  }

  public add<R extends DBRecord>(
    storeName: StoreName,
    data: R
  ): Promise<IndexDBResult<IDBRequest, Error>> {
    const db: IDBDatabase = this.mainDb as IDBDatabase;
    const requestForDebuging: IndexDBRequest = {
      actionType: dbAction.add,
      storeName,
      data,
    };
    return this.indexDbAction({
      actionType: dbAction.add,
      data,
      storeName,
    });
  }

  public get<R extends DBRecord>(storeName: StoreName, data: R): Promise<any> {
    const db: IDBDatabase = this.mainDb as IDBDatabase;
    return this.indexDbAction({
      actionType: dbAction.get,
      data,
      storeName,
    });
  }

  public getAll<R extends DBRecord>(
    storeName: StoreName,
    data?: R
  ): Promise<any> {
    const db: IDBDatabase = this.mainDb as IDBDatabase;
    return this.indexDbAction({
      actionType: dbAction.getAll,
      data: data || {},
      storeName,
    });
  }

  public clear<R extends DBRecord>(
    storeName: StoreName,
    data?: R
  ): Promise<any> {
    const db: IDBDatabase = this.mainDb as IDBDatabase;
    return this.indexDbAction({
      actionType: dbAction.clear,
      data: data || {},
      storeName,
    });
  }

  public delete<R extends DBRecord>(
    storeName: StoreName,
    data: R
  ): Promise<any> {
    const db: IDBDatabase = this.mainDb as IDBDatabase;
    return this.indexDbAction({
      actionType: dbAction.delete,
      data,
      storeName,
    });
  }

  public put<R extends DBRecord>(storeName: StoreName, data: R): Promise<any> {
    const db: IDBDatabase = this.mainDb as IDBDatabase;
    return this.indexDbAction({
      actionType: dbAction.put,
      data,
      storeName,
    });
  }

  private initDB(): Promise<IDBDatabase | Error> {
    return this.openDBRequest(this.MAIN_DB_NAME);
  }

  private timer(
    time: number,
    valueToReturn: any = true
  ): Promise<boolean | Error> {
    return new Promise((res, rej) => {
      try {
        setTimeout((_) => res(valueToReturn), time);
      } catch (e) {
        // tslint:disable-next-line:no-console
        console.warn(e);
        rej(e);
      }
    });
  }

  private openDBRequest(dbName: string): Promise<IDBDatabase | Error> {
    const openReq: IDBOpenDBRequest = window.indexedDB.open(
      dbName,
      IndexDbService.versionNumber
    );
    return new Promise<IDBDatabase | Error>((resolve, reject) => {
      openReq.addEventListener("success", (event: DBOpenEvent) => {
        const db: IDBDatabase | undefined =
          (event && event.target && event.target.result) || undefined;
        if (!db) {
          reject(new Error("Error open request failed"));
          return;
        }
        try {
          resolve(db);
        } catch (e) {
          reject(new Error(e.toString()));
        }
      });
      openReq.addEventListener("upgradeneeded", (event: DBOpenEvent) => {
        const db: IDBDatabase | undefined =
          (event && event.target && event.target.result) || undefined;
        if (!db) {
          this.dBError("Error open request failed");
          return;
        }
        this.allStores.forEach((storeName: StoreName) => {
          if (!db.objectStoreNames.contains(storeName)) {
            this.createObjectStoreOnUpgrade(db, storeName);
          }
        });

        // tslint:disable-next-line:no-console
        console.info(
          `indexDb upgrade needed. Upgrading to version ${event.target.result.version}`
        );
      });
      openReq.addEventListener("error", () => {
        this.dBError("Error open request failed");
      });
    });
  }

  private dBError(error: any) {
    let e = error;
    if (typeof error !== "string") {
      e = error.toString();
    }
    throw new Error(e);
  }

  private getTransaction(db: IDBDatabase): Promise<IDBTransaction> {
    return new Promise((res, rej) => {
      if (!db) {
        throw new Error("No DB Object");
      }
      const trans = db.transaction(this.allStoreNames, "readwrite");
      trans.onerror = (e) => rej(e);
      res(trans);
    });
  }

  private getStore(
    storeName: StoreName | undefined,
    transaction: IDBTransaction
  ): Promise<IDBObjectStore> {
    return new Promise((res, rej) => {
      if (!storeName) {
        storeName = this.MAIN_STORE_NAME;
      }
      if (!this.mainDb) {
        return new Error("No DB Object");
      }

      const store: IDBObjectStore = transaction.objectStore(storeName);
      res(store);
    });
  }

  private makeRequest<R extends DBRecord>(
    store: IDBObjectStore,
    reqType: dbAction,
    record: Partial<R>
  ): Promise<IndexDBResult<IDBRequest, Error>> {
    const requestForDebuging: IndexDBRequest = {
      actionType: dbAction.add,
      storeName: store.name as StoreName,
      data: record,
    };
    return new Promise((res, rej) => {
      let key: IDBValidKey | undefined = record.id;
      if (IndexDbService.Config.IdsCanOnlyBeStrings && key && +key > 0) {
        key = key.toString();
      }
      let request: IDBRequest;
      switch (reqType) {
        case dbAction.add:
          if (!record.id) {
            record.id = this.newId;
          } else if (IndexDbService.Config.IdsCanOnlyBeStrings) {
            record.id = record.id.toString();
          }

          request = store.add(record);
          break;
        case dbAction.delete:
          if (!key) {
            throw new Error("No key was found for delete");
          }
          request = store.delete(key);
          break;
        case dbAction.clear:
          request = store.clear();

          break;
        case dbAction.put:
          if (!record.id) {
            record.id = this.newId;
          } else if (IndexDbService.Config.IdsCanOnlyBeStrings) {
            record.id = record.id.toString();
          }
          request = store.put(record);
          break;
        case dbAction.get:
          if (!key) {
            throw new Error("No key was found for get");
          }
          request = store.get(key);
          break;
        case dbAction.getAll:
          // @ts-ignore
          request = store.getAll();
          break;
      }

      request.onsuccess = (ev: Event) => {
        res({
          actionRequested: requestForDebuging,
          success: true,
          data: request,
        });
      };
      request.onerror = (ev: Event) => {
        const err = new Error((ev as any)["target"]["error"]["message"]);
        const errorEvent = { target: { error: err } };
        rej({
          actionRequested: requestForDebuging,
          success: false,
          error: errorEvent,
        });
      };
    });
  }

  private createObjectStoreOnUpgrade(db: IDBDatabase, storeName: string): void {
    if (db.objectStoreNames.contains(storeName)) {
      return;
    }
    db.createObjectStore(storeName, {
      keyPath: "id",
      autoIncrement: true,
    });
  }

  private indexDbAction<R>(
    actionParams: IndexDBRequest
  ): Promise<IndexDBResult<IDBRequest, Error>> {
    const { storeName, data, actionType } = actionParams;
    return this.getTransaction(this.mainDb as IDBDatabase)
      .then((trans) => this.getStore(storeName, trans))
      .then((store: IDBObjectStore) =>
        this.makeRequest(store, actionType, data)
      )
      .then((res: IndexDBResult<IDBRequest, Error>) => res)
      .catch(
        (
          originalErr:
            | Error
            | {
                actionRequested?: any;
                success: false;
                data?: any;
                error: Error;
              }
        ) => {
          let errorTothrow:
            | Error
            | {
                actionRequested?: any;
                success: false;
                data?: any;
                error: Error;
              } = originalErr;
          let extendedErr: {
            actionRequested?: any;
            success: false;
            data?: any;
            error: any;
          };
          if (originalErr instanceof Error) {
            extendedErr = {
              success: false,
              error: {
                target: {
                  error: originalErr,
                },
              },
            };
          } else {
            extendedErr = originalErr;
          }
          try {
            const errorEvent: any = extendedErr.error;
            const onlyError: Error = errorEvent.target.error;
            extendedErr = { ...extendedErr, actionRequested: actionParams };

            extendedErr.actionRequested["dataBase"] = this.MAIN_DB_NAME;
            const errorData = this.buildExtendedErrorData(extendedErr);
            errorTothrow = new ExtendedError(onlyError, errorData);

            throw errorTothrow;
          } catch (e) {
            throw errorTothrow;
          }
        }
      );
  }
}



// WEBPACK FOOTER //
// ./src/app/_Database/index-db-service.ts